import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { api, fileUrl, formatDMY } from "@/lib/api";
import {
  ArrowLeft,
  User,
  Phone,
  Home,
  Calendar,
  Wallet,
  Cake,
  Droplet,
  Utensils,
  GraduationCap,
  BookOpen,
  MapPin,
  FileText,
  IdCard,
  ExternalLink,
} from "lucide-react";

export default function StudentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [complaints, setComplaints] = useState([]);

  useEffect(() => {
    api.get(`/students/${id}`).then((r) => setS(r.data)).catch(() => nav("/admin"));
    api.get("/complaints").then((r) => setComplaints(r.data.filter((c) => c.student_id === id)));
  }, [id, nav]);

  if (!s) {
    return (
      <DashboardShell title="Loading..." testId="student-detail-page">
        <div />
      </DashboardShell>
    );
  }

  const paid = s.dues <= 0;
  const displayName = s.surname ? `${s.surname} ${s.full_name}` : s.full_name;

  return (
    <DashboardShell title={displayName} subtitle={`Resident · ${s.student_id_number}`} testId="student-detail-page">
      <button
        onClick={() => nav("/admin")}
        data-testid="back-to-admin"
        className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white mb-6 hover:shadow-brutal-sm transition-shadow"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="brutal-card p-6 bg-white lg:col-span-1">
          {s.photo_path ? (
            <img
              src={fileUrl(s.photo_path)}
              alt={displayName}
              className="w-full h-64 object-cover border-2 border-[#0A0A0A] rounded-xl"
            />
          ) : (
            <div className="w-full h-64 flex items-center justify-center border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600]">
              <User size={80} strokeWidth={2} />
            </div>
          )}
          <h2 className="font-heading font-black text-2xl mt-4 tracking-tighter">{displayName}</h2>
          <div className="mt-1 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">
            ID: {s.student_id_number}
            {s.kl_id && <> · KL/Admn: {s.kl_id}</>}
          </div>

          {(s.aadhar_path || s.university_id_path) && (
            <div className="mt-4 space-y-2" data-testid="student-documents">
              {s.aadhar_path && (
                <a
                  href={fileUrl(s.aadhar_path)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-2 px-3 py-2 border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600] font-bold text-sm hover:shadow-brutal-sm transition-shadow"
                >
                  <span className="flex items-center gap-2"><FileText size={14} strokeWidth={2.5} /> Aadhar</span>
                  <ExternalLink size={14} strokeWidth={2.5} />
                </a>
              )}
              {s.university_id_path && (
                <a
                  href={fileUrl(s.university_id_path)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-2 px-3 py-2 border-2 border-[#0A0A0A] rounded-xl bg-[#00E5FF] font-bold text-sm hover:shadow-brutal-sm transition-shadow"
                >
                  <span className="flex items-center gap-2"><IdCard size={14} strokeWidth={2.5} /> University/Employee ID</span>
                  <ExternalLink size={14} strokeWidth={2.5} />
                </a>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoTile icon={Home} label="Room" value={`${s.flat_number || "—"}${s.sub_room ? " / " + s.sub_room : ""}`} />
            <InfoTile icon={Calendar} label="Date of Joining" value={formatDMY(s.date_of_joining)} />
            <InfoTile icon={Cake} label="Date of Birth" value={formatDMY(s.date_of_birth)} />
            <InfoTile icon={Droplet} label="Blood Group" value={s.blood_group} />
            <InfoTile icon={GraduationCap} label="Year" value={s.year} />
            <InfoTile icon={BookOpen} label="Branch" value={s.branch} />
            <InfoTile icon={Phone} label="Student Contact" value={s.contact_number} />
            <InfoTile icon={Phone} label="Parent Contact" value={s.parent_contact_number} />
            <InfoTile icon={User} label="Parent Name" value={s.parent_full_name} />
            <InfoTile icon={Utensils} label="Food Preference" value={s.food_preference} />
            {s.residential_address && (
              <div className="brutal-card p-5 bg-white sm:col-span-2">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                  <MapPin size={14} strokeWidth={2.5} /> Residential Address
                </div>
                <div className="font-heading font-bold text-base mt-1 whitespace-pre-line">
                  {s.residential_address}
                </div>
              </div>
            )}
          </div>

          <div className="brutal-card p-6 bg-white">
            <div className="flex items-center gap-2 mb-4">
              <Wallet size={22} strokeWidth={2.5} />
              <h3 className="font-heading font-black text-2xl">Fees</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FeeBox label="Total" value={`₹${Number(s.total_fees || 0).toLocaleString()}`} tone="white" />
              <FeeBox label="Paid" value={`₹${Number(s.fees_paid || 0).toLocaleString()}`} tone="green" />
              <FeeBox
                label={paid ? "Paid in full" : "Dues"}
                value={paid ? "✓" : `₹${Number(s.dues).toLocaleString()}`}
                tone={paid ? "green" : "coral"}
              />
            </div>
          </div>

          <div className="brutal-card p-6 bg-white">
            <h3 className="font-heading font-black text-2xl mb-4">Complaints history</h3>
            {complaints.length === 0 ? (
              <p className="font-medium text-[#4A4A4A]">No complaints raised.</p>
            ) : (
              <ul className="space-y-3">
                {complaints.map((c) => (
                  <li key={c.id} className="border-2 border-[#0A0A0A] rounded-xl p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600]">
                        {c.category}
                      </span>
                      <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF]">
                        {c.status}
                      </span>
                      <span className="text-xs font-medium text-[#4A4A4A]">
                        {c.date_of_complaint}
                      </span>
                    </div>
                    <p className="mt-2 font-medium">{c.description}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="brutal-card p-5 bg-white">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
        <Icon size={14} strokeWidth={2.5} /> {label}
      </div>
      <div className="font-heading font-bold text-lg mt-1">{value || "—"}</div>
    </div>
  );
}

function FeeBox({ label, value, tone }) {
  const tones = {
    white: { bg: "#FFFFFF", fg: "#0A0A0A" },
    green: { bg: "#00CC66", fg: "#FFFFFF" },
    coral: { bg: "#FF4D4D", fg: "#FFFFFF" },
  };
  const t = tones[tone];
  return (
    <div
      style={{
        backgroundColor: t.bg, color: t.fg,
        border: "2px solid #0A0A0A", borderRadius: "0.75rem", padding: "1rem",
      }}
    >
      <div className="text-xs font-bold uppercase tracking-widest">{label}</div>
      <div className="font-heading font-black text-2xl mt-1">{value}</div>
    </div>
  );
}
